import { useCallback } from "react";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import type { NurseInShift } from "../../utils/models";
import { getShiftIndexByShiftTimes } from "../../utils/utilFunctions";

export const useUpdateLocationAndHoursForShift = () => {
	const { getWeekIndexByDateTime, getDayIndexByDateTime, chosenDepartment, updateNurseLocationAndTimes } = useHospitalData();
	const { updateNurse, isLoading: isLoadingTypes } = useHandleNurseChange();
	const { updateNurse: updateNurseLocationOrTimesLoading } = isLoadingTypes;
	const { isDraftMode } = useDraftMode();

	const updateLocationAndHours = useCallback(
		async ({ updatedNurseInShift, originalNurseInShift }: { updatedNurseInShift: NurseInShift; originalNurseInShift: NurseInShift }): Promise<boolean> => {
			// getDayIndexByDateTime/getWeekIndexByDateTime already apply getShiftDisplayDate internally.
			// Do NOT wrap with getShiftDisplayDate here — double application shifts the date twice
			// for PM dayStart when the start time falls exactly at the dayStart hour.
			const dayNo = getDayIndexByDateTime(updatedNurseInShift.startTime);
			const weekIndex = getWeekIndexByDateTime(updatedNurseInShift.startTime);
			const shiftIndex = getShiftIndexByShiftTimes({
				startTime: updatedNurseInShift.startTime,
				endTime: updatedNurseInShift.endTime,
			});
			const updatedNurseInShiftWithShiftType = {
				...updatedNurseInShift,
				shiftType: chosenDepartment.shift_types[shiftIndex],
			};
			const didChangedTimes =
				originalNurseInShift.startTime !== updatedNurseInShiftWithShiftType.startTime ||
				originalNurseInShift.endTime !== updatedNurseInShiftWithShiftType.endTime;
			const didChangeLocation = originalNurseInShift.location !== updatedNurseInShiftWithShiftType.location;
			const didChangeJob = originalNurseInShift.jobId !== updatedNurseInShiftWithShiftType.jobId;
			if (!didChangedTimes && !didChangeLocation && !didChangeJob) {
				return false;
			}
			const previousShiftIndex = getShiftIndexByShiftTimes({
				startTime: originalNurseInShift.startTime,
				endTime: originalNurseInShift.endTime,
			});
			let success: boolean;
			try {
				await updateNurse({
					nurse: updatedNurseInShiftWithShiftType,
					shiftIndex,
					weekIndex,
					dayNo,
				});
				success = true;
			} catch {
				//error handled inside mutate
				success = false;
			}
			if (!success) {
				return false;
			}
			updateNurseLocationAndTimes({
				nurseInShiftId: originalNurseInShift.id,
				weekIndex,
				shiftIndex: previousShiftIndex,
				dayIndex: dayNo,
				actual: false,
				newStartTime: didChangedTimes ? updatedNurseInShiftWithShiftType.startTime : undefined,
				newEndTime: didChangedTimes ? updatedNurseInShiftWithShiftType.endTime : undefined,
				newLocation: didChangeLocation ? updatedNurseInShiftWithShiftType.location : undefined,
				newJobId: didChangeJob ? updatedNurseInShiftWithShiftType.jobId : undefined,
				newJobName: didChangeJob ? updatedNurseInShiftWithShiftType.jobName : undefined,
				isDraftMode,
			});
			return true;
		},
		[updateNurse, getDayIndexByDateTime, getWeekIndexByDateTime, chosenDepartment, isDraftMode, updateNurseLocationAndTimes],
	);

	return { updateLocationAndHours, updateNurseLocationOrTimesLoading };
};
